import React, { useLayoutEffect, useRef, useState } from "react";
import {
    GoogleLogin,
    GoogleOAuthProvider,
    type CredentialResponse,
} from "@react-oauth/google";

type AuthGoogleButtonProps = {
    onSuccess: (res: CredentialResponse) => void;
    onError: () => void;
    text: "signin_with" | "signup_with";
};

/** Google Identity Services renders at a fixed pixel width, clamped to 200–400. */
const GSI_MIN_WIDTH = 200;
const GSI_MAX_WIDTH = 400;

/** How long the column must hold still before the widget is re-rendered. */
const RESIZE_SETTLE_MS = 150;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

/**
 * The "Continue with Google" slot.
 *
 * The prototype draws its own 52px `.btn-google`, but we keep Google's own GSI
 * widget: only it returns the ID-token `credential` that POST
 * /auth/google-login/ verifies. A custom button would mean moving the backend
 * to an auth-code exchange, which is out of scope for a restyle.
 *
 * GSI takes a fixed pixel `width` and will not reflow, so the slot measures
 * itself and re-mounts the widget (via `key`) when the column resizes —
 * otherwise the 400px desktop button is clipped by the phone layout's 335px
 * column.
 */
function GoogleIdentityButton({ onSuccess, onError, text }: AuthGoogleButtonProps) {
    const slotRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<number | null>(null);

    useLayoutEffect(() => {
        const el = slotRef.current;
        if (!el) return;

        const measure = () => {
            const available = Math.floor(el.getBoundingClientRect().width);
            setWidth(Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, available)));
        };

        // First measure is immediate, so the button never paints at the wrong
        // width. Later ones are debounced: changing `width` re-mounts the GSI
        // widget, and an undebounced observer would re-initialise it once per
        // pixel while a window is being dragged.
        measure();

        let settle: ReturnType<typeof setTimeout> | null = null;
        const observer = new ResizeObserver(() => {
            if (settle) clearTimeout(settle);
            settle = setTimeout(measure, RESIZE_SETTLE_MS);
        });
        observer.observe(el);

        return () => {
            observer.disconnect();
            if (settle) clearTimeout(settle);
        };
    }, []);

    return (
        <div className="auth-google-slot" ref={slotRef}>
            {width === null ? null : (
                <GoogleLogin
                    key={width}
                    onSuccess={onSuccess}
                    onError={onError}
                    theme="outline"
                    shape="rectangular"
                    size="large"
                    text={text}
                    width={String(width)}
                    locale="en"
                />
            )}
        </div>
    );
}

export default function AuthGoogleButton(props: AuthGoogleButtonProps) {
    return (
        <GoogleOAuthProvider
            clientId={GOOGLE_CLIENT_ID}
            onScriptLoadError={props.onError}
        >
            <GoogleIdentityButton {...props} />
        </GoogleOAuthProvider>
    );
}
